import { useState, useEffect } from 'react';
import axios from 'axios';
import { Database, Table as TableIcon } from 'lucide-react';
import styles from '../styles/DatabaseViewer.module.css';
import Skeleton from "../components/ui/Skeleton";
import SelectDropdown from "../components/ui/SelectDropdown";
import { EmptyState, ErrorCard } from "../components/ui/FeedbackStates";

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const DatabaseViewer = () => {
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState('');
  const [tableData, setTableData] = useState([]);
  const [loadingTables, setLoadingTables] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState(null);

  // Fetch tables on component mount
  useEffect(() => {
    const fetchTables = async () => {
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(`${API_BASE_URL}/api/database/tables`, { headers });
        if (response.data && response.data.success) {
          setTables(response.data.tables);
          if (response.data.tables.length > 0) {
            setSelectedTable(response.data.tables[0]);
          }
        } else {
          setError('Failed to fetch tables list');
        }
      } catch (err) {
        console.error('Error fetching tables:', err);
        setError('Error connecting to the server. ' + (err.response?.data?.message || err.message));
      } finally {
        setLoadingTables(false);
      }
    };

    fetchTables();
  }, []);

  // Fetch data when a table is selected
  useEffect(() => {
    if (!selectedTable) return;

    const fetchData = async () => {
      setLoadingData(true);
      setError(null);
      
      try {
        const token = localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        
        const response = await axios.get(`${API_BASE_URL}/api/database/data/${selectedTable}`, { headers });
        if (response.data && response.data.success) {
          setTableData(response.data.data);
        } else {
          setError(`Failed to fetch data for ${selectedTable}`);
        }
      } catch (err) {
        console.error(`Error fetching data for ${selectedTable}:`, err);
        setError(`Error fetching data for ${selectedTable}. ` + (err.response?.data?.message || err.message));
      } finally {
        setLoadingData(false);
      }
    };

    fetchData();
  }, [selectedTable]);

  const handleTableChange = (e) => {
    setSelectedTable(e.target.value);
  };

  const getHeaders = () => {
    if (tableData.length === 0) return [];
    // Extract unique keys from all objects to ensure all columns are represented
    const allKeys = new Set();
    tableData.forEach(item => {
      Object.keys(item).forEach(key => allKeys.add(key));
    });
    return Array.from(allKeys);
  };

  const headers = getHeaders();

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>
          <Database className="inline-block mr-2 text-blue-600" size={28} style={{ verticalAlign: 'text-bottom' }} />
          Database Explorer
        </h1>
        
        <div className={styles.controls}>
          <div style={{ minWidth: "240px" }}>
            <SelectDropdown
              id="table-select"
              value={selectedTable}
              onChange={handleTableChange}
              disabled={loadingTables || tables.length === 0}
              placeholder={tables.length === 0 ? "No tables found" : "Select table..."}
              options={tables.map((table) => ({ value: table, label: table }))}
            />
          </div>
        </div>
      </header>

      {error && (
        <div style={{ marginBottom: "16px" }}>
          <ErrorCard title="Database Error" message={error} />
        </div>
      )}

      {loadingTables ? (
        <Skeleton variant="list" count={4} />
      ) : (
        <div className={styles.tableContainer}>
          {loadingData ? (
            <div className={styles.loading}>Fetching records...</div>
          ) : tableData.length === 0 ? (
            <div style={{ padding: "40px 16px" }}>
              <EmptyState
                icon={<TableIcon size={32} />}
                title="No data found"
                description={`The table "${selectedTable}" is currently empty.`}
              />
            </div>
          ) : (
            <table className={styles.table}>
              <thead>
                <tr>
                  {headers.map((header) => (
                    <th key={header} className={styles.th}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableData.map((row, index) => (
                  <tr key={index} className={styles.tr}>
                    {headers.map((header) => {
                      const value = row[header];
                      let displayValue = value;
                      
                      // Format objects/arrays to string
                      if (typeof value === 'object' && value !== null) {
                        displayValue = JSON.stringify(value);
                      } else if (typeof value === 'boolean') {
                        displayValue = value ? 'true' : 'false';
                      }
                      
                      return (
                        <td key={`${index}-${header}`} className={styles.td} title={String(displayValue)}>
                          {String(displayValue ?? 'null')}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
};

export default DatabaseViewer;
